import type { InfiniteData, QueryKey } from "@tanstack/vue-query";
import { type Ref, toRef } from "vue";

import type { GenericModel } from "@/lib/tanstack-table";
import type { Newsletter } from "@/types/newsletter";
import { useStore as useScaffoldingStore } from "@/store/stores/scaffolding";

import type { ListingPage } from "./generic";

export const useNewsletter = (): Ref<Newsletter | undefined> => {
  const scaffoldingStore = useScaffoldingStore();
  return toRef(() => scaffoldingStore.newsletter) as Ref<
    Newsletter | undefined
  >;
};

export const useNewsletterId = (): Ref<string | undefined> => {
  const newsletter = useNewsletter();
  return toRef(() => newsletter.value?.id);
};

export const filterInfiniteQueries = <T extends GenericModel>(
  queries: Array<[QueryKey, InfiniteData<ListingPage<T>> | undefined]>,
  predicate: (item: T) => boolean
): T[] => {
  const found: T[] = [];
  const ids = new Set<string>();

  for (const [, data] of queries) {
    if (!data) {
      continue;
    }

    for (const page of data.pages) {
      for (const item of page.results) {
        if (ids.has(item.id)) {
          continue;
        }

        if (predicate(item)) {
          ids.add(item.id);
          found.push(item);
        }
      }
    }
  }

  return found;
};

export const findItemInListQueries: {
  <T extends GenericModel, S extends T>(
    queries: Array<[QueryKey, T[] | undefined]>,
    predicate: (item: T) => item is S
  ): S | undefined;
  <T extends GenericModel>(
    queries: Array<[QueryKey, T[] | undefined]>,
    predicate: (item: T) => boolean
  ): T | undefined;
} = (
  queries: Array<[QueryKey, GenericModel[] | undefined]>,
  predicate: (item: GenericModel) => boolean
): GenericModel | undefined => {
  for (const [, data] of queries) {
    if (!data) {
      continue;
    }

    for (const item of data) {
      if (predicate(item)) {
        return item;
      }
    }
  }
};

export const findItemInInfiniteQueries = <T extends GenericModel>(
  queries: Array<[QueryKey, InfiniteData<ListingPage<T>> | undefined]>,
  predicate: (item: T) => boolean
): T | undefined => {
  for (const [, data] of queries) {
    if (!data) {
      continue;
    }

    for (const page of data.pages) {
      for (const item of page.results) {
        if (predicate(item)) {
          return item;
        }
      }
    }
  }
};
